import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import api from "../lib/api";
import { Loader2 } from "lucide-react";

const TFS = ["5m", "15m", "1h", "4h", "1d"];
const W = 640, H = 260, PADX = 8, PADY = 12, AXW = 56;

export default function CandleChart({ symbol, entry, stop_loss, take_profit_1, take_profit_2 }) {
  const { t } = useLang();
  const [tf, setTf] = useState("1h");
  const [candles, setCandles] = useState([]);
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/market/candles/${symbol.replace("/", "-")}?timeframe=${tf}`)
      .then((r) => { if (alive) { setCandles(r.data.candles.slice(-70)); setPrice(r.data.price); } })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [symbol, tf]);

  if (loading) return <div className="h-[220px] flex items-center justify-center text-[#6B7280]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!candles.length) return null;

  const levels = [entry, stop_loss, take_profit_1, take_profit_2].filter((x) => x != null);
  const hi = Math.max(...candles.map((c) => c.high), ...levels);
  const lo = Math.min(...candles.map((c) => c.low), ...levels);
  const range = hi - lo || 1;
  const plotW = W - AXW - PADX;
  const plotH = H - PADY * 2;
  const y = (p) => PADY + (hi - p) / range * plotH;
  const cw = plotW / candles.length;
  const bw = Math.max(cw * 0.6, 1.5);

  const line = (p, color, label) => {
    if (p == null) return null;
    const yy = y(p);
    return (
      <g key={label}>
        <line x1={PADX} x2={W - AXW} y1={yy} y2={yy} stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.8" />
        <rect x={W - AXW} y={yy - 8} width={AXW} height="16" fill={color} />
        <text x={W - AXW / 2} y={yy + 3.5} textAnchor="middle" fontSize="9" fontFamily="IBM Plex Mono" fill="#05050A" fontWeight="600">{Number(p).toFixed(getDec(p))}</text>
      </g>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-mono">
          <span className="font-display font-bold text-sm">{symbol}</span>
          {price != null && <span className="text-[#9CA3AF]">{price}</span>}
        </div>
        <div className="flex border border-[#1E2028]">
          {TFS.map((x) => (
            <button key={x} data-testid={`tf-${x}`} onClick={() => setTf(x)}
              className={`px-2 py-1 text-[10px] font-mono transition-colors duration-200 ${tf === x ? "bg-[#FACC15] text-[#05050A]" : "text-[#9CA3AF] hover:text-white"}`}>
              {x}
            </button>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ background: "#05050A" }} data-testid="candle-chart">
        {candles.map((c, i) => {
          const x = PADX + i * cw + (cw - bw) / 2;
          const up = c.close >= c.open;
          const color = up ? "#10B981" : "#EF4444";
          const cx = PADX + i * cw + cw / 2;
          const yO = y(c.open), yC = y(c.close);
          const top = Math.min(yO, yC);
          const hgt = Math.max(Math.abs(yC - yO), 1);
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={y(c.high)} y2={y(c.low)} stroke={color} strokeWidth="1" />
              <rect x={x} y={top} width={bw} height={hgt} fill={color} />
            </g>
          );
        })}
        {line(take_profit_2, "#10B981", "tp2")}
        {line(take_profit_1, "#22C55E", "tp1")}
        {line(entry, "#FACC15", "entry")}
        {line(stop_loss, "#EF4444", "sl")}
      </svg>
    </div>
  );
}

function getDec(p) {
  const a = Math.abs(p);
  if (a >= 1000) return 1;
  if (a >= 10) return 2;
  if (a >= 1) return 4;
  return 5;
}
